// REACT
import React = require("react");

// LOCAL
import {MarketingTabMapper} from "../locations/marketing/utils/MarketingTabMapper";
import {ColorUtils} from "../components/Utils/color/ColorUtils";
import {Loader} from "../components/Utils/loader/Loader";
import {DashboardWidgetList} from "./DashboardWidgetList";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../index";

// INTERFACES
interface DashboardResponse {
    texts: DashboardConfigurationTexts;
    widgets: WidgetResponse[];
}

export interface StandardDashboardTexts {
    equipmentDistributionWidgetTitle: string;
    dispenseCoffeeTimeWidgetTitle: string;
    comparativeCoffeeSalesWidgetTitle: string;
    lastAlarmsWidgetTitle: string;
    lastActivitiesWidgetTitle: string;
    activeEquipmentAndLocationsLastWeekTitle: string;
    equipmentStockStatus: string;
    equipmentEfficiency: string;
}

export interface AssetBaseWidgetTexts {
    title: string;
    titleExplanation: string;
    manufacturersTitle: string;
    manufacturersTitleExplanation: string;
    equipmentWithTelemetry: string;
    equipmentAtManufacturers: string;
    equipmentNotAtManufacturers: string;
    totals: string;
}

export interface NotAtManufacturersWidgetTexts {
    title: string;
    titleExplanation: string;
    equipmentByFamily: string;
    equipmentWithTelemetry: string;
    equipmentOnWarehouse: string;
    equipmentOnMarket: string;
    equipmentInUnknownLocation: string;
    others: string;
    noData: string;
}

export interface MarketWidgetTexts {
    title: string;
    titleExplanation: string;
    equipmentByFamily: string;
    equipmentOnMarket: string;
    unauthorizedEquipmentMovementCount: string;
    others: string;
    noData: string;
    registered: string;
    unregistered: string;
}

export interface EquipmentConnectivityTexts {
    title: string;
    registeredActiveEquipment: string;
    unregisteredActiveEquipment: string;
    registeredOffEquipment: string;
    unregisteredOffEquipment: string;
    registeredUnreachableEquipment: string;
    unregisteredUnreachableEquipment: string;
}

export interface EquipmentPlacementTexts {
    title: string;
    titleExplanation: string;
    equipmentOnWarehouse: string;
    equipmentOnMarket: string;
    equipmentInUnknownLocation: string;
    equipmentAtManufacturers: string;
    others: string;
}

export interface EquipmentTypeTexts {
    title: string;
    titleExplanation: string;
    others: string;
    noData: string;
}

export interface EquipmentMovementsTexts {
    title: string;
    titleExplanation: string;
    unknownToMarket: string;
    marketToUnknown: string;
    warehouseToMarket: string;
    internalMovements: string;
}

export interface TimeTexts {
    years: string;
    year: string;
    months: string;
    month: string;
    days: string;
    day: string;
    hours: string;
    hour: string;
    minutes: string;
    minute: string;
}

export interface AlarmsOpenedTexts {
    title: string;
    titleExplanation: string;
    equipmentWithAlarms: string;
    alarmsByType: string;
    averageAlarmsByEquipment: string;
    openedAlarmsAverageAge: string;
    others: string;
    noData: string;
    of: string;
    on: string;
    timeTexts: TimeTexts;
}

export interface AlarmsDistributionTexts {
    title: string;
    titleExplanation: string;
    noData: string;
    others: string;
}

export interface AlarmsManagementTexts {
    title: string;
    titleExplanation: string;
    openedClosedAlarmsLast2Weeks: string;
    openedAlarmsMore2Weeks: string;
    openedAlarmsLess2Weeks: string;
    openedAlarms: string;
    closedAlarms: string;
    alarmsUnitOfMeasure: string;
    openedAlarmsAverageAge: string;
    closedAlarmsAverageAge: string;
    closedAlarmsLess2Weeks: string;
    timeTexts: TimeTexts;
}

export interface UnileverDashboardTexts {
    assetBase: AssetBaseWidgetTexts;
    notAtManufacturers: NotAtManufacturersWidgetTexts;
    market: MarketWidgetTexts;
    placement: EquipmentPlacementTexts;
    equipmentTypes: EquipmentTypeTexts;
    movements: EquipmentMovementsTexts;
    alarmsOpened: AlarmsOpenedTexts;
    alarmsDistribution: AlarmsDistributionTexts;
    alarmsManagement: AlarmsManagementTexts;
    equipmentConnectivity: EquipmentConnectivityTexts;
}

export interface DashboardConfigurationTexts {
    dashboardTitle: string;
    standard: StandardDashboardTexts;
    unilever: UnileverDashboardTexts;
}

interface DynamicConfiguration {
    guids: string[];
}

export interface WidgetResponse {
    type: string;
    dataUrl?: string;
    dynamicConfiguration?: DynamicConfiguration;
    hideOnLoading: boolean;
}

const DashboardContainerWaiting: React.SFC = (props) => {
    return (
        <Loader/>
    );
};

// DASHBOARD CONTAINER

interface DashboardContainerProps {
    stackManager: any;
}

interface DashboardContainerState {
    active: boolean;
    response: DashboardResponse;
}

export enum CategoriesColors {
    PLACEMENT = "placements",
    ALARM_TYPES = "alarm_types",
    COMPONENT_CATEGORIES = "component_categories",
    COMPONENT_MODELS = "component_models",
}

export enum Placements {
    AT_MANUFACTURERS = "2f7629f2-247a-11e9-ba62-941882820f48",
    IN_WAREHOUSE = "3cd3350e-247a-11e9-ba62-941882820f48",
    ON_MARKET = "484e4004-247a-11e9-ba62-941882820f48",
    IN_UNKNOWN_LOCATION = "4b83954e-247a-11e9-ba62-941882820f48",
    NOT_AT_MANUFACTURERS = "c83052a0-2af9-11e9-b596-b2a6d5960774",
}

export const DASHBOARD_COLORS = {
    placements: [
        "#1F4E5F",
        "#79a8a9",
        "#aacfd0",
        "#badfe0",
    ],
    alarm_types: [
        "#F3D563",
        "#F3B05A",
        "#F46A4E",
        "#A3586D",
        "#5C4A72",
        "#c6e5d9",
        "#8FBC94",
        "#9B8281",
        "#c973cb",
        "#D1B6E1",
        "#dedcee",
        "#3B8281",
    ],
    component_categories: [
        "#5CAB7D",
        "#C03546",
        "#566eb8",
        "#c687bf",
        "#29a3aa",
        "#22294F",
        "#c57711",
    ],
};

export class DashboardContainer extends React.Component<DashboardContainerProps, DashboardContainerState> {

    public static codifyEntityName(name: string): string {
        if (name == null) {
            return null;
        }
        return MarketingTabMapper.codifyEntityName(name);
    }

    constructor(props: DashboardContainerProps) {
        super(props);
        this.state = {
            active: true,
            response: undefined,
        };

        this.retrieveData();
    }

    public gatherContent() {

        if (typeof this.state.response === "undefined") {
            return (
                <DashboardContainerWaiting/>
            );
        }

        return (
            <div className="f-grid dashboard-container">
                <DashboardWidgetList
                    data={this.state.response.widgets}
                    configuration={this.state.response.texts}
                    stackManager={this.props.stackManager}
                    colorGenerator={new ColorUtils(DASHBOARD_COLORS)}
                />
            </div>
        );
    }

    public render() {
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div className="dashboard-page">
                    {this.gatherContent()}
                </div>
            </ThemeProvider>
        );
    }

    /**
     * Retrieve the updated last sign of life and change the component state accordingly.
     */
    private retrieveData(): void {
        Ajax.vbxAjax({
            url: "dashboard-configuration",
            type: "GET",
            cache: false,
            success: (response: DashboardResponse) => {
                if (response.widgets) {
                    this.setState({
                        response,
                    });
                }
            },
        });
    }
}
